import React, { useCallback, useState } from "react";
import { FileDown, FileText, FileStack, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SectionCard } from "@/components/jackstone/SectionCard";
import { buildGanttExportPayload } from "@/components/gantt/ganttTypes";
import { buildCalculatorPdfPayloadLive } from "@/lib/calculatorPdf";
import { http, LONG_TIMEOUT_MS, getClientId, saveScenario } from "@/lib/api";
import { useSettings } from "@/hooks/useSettings";

interface ExportReportCardProps {
  /** Model code shown in the model preview (e.g. "VPF 28-810-600-102-1D"). */
  modelCode?: string;
  /** Freezer count shown in the model preview. */
  nFreezers?: string | number;
  /** Enable the buttons once a calculation/simulation result exists. */
  ready: boolean;
  /** False while the inputs are invalid / the result is out of date — the
   *  buttons stay disabled so a report is never built on stale numbers. */
  current?: boolean;
  /** Originating page — recorded on the saved scenario. */
  page?: "calculator" | "gantt";
}

// ── Brand button styles (Jackstone palette · soft = tinted bg + coloured
//    text, solid = full fill + white text). Single source of truth. ─────────
const BTN_BASE =
  "flex w-full items-center justify-center gap-1.5 h-9 rounded-md text-[12px] font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
const BTN_SOFT_PURPLE =
  "bg-[#ECEBF7] text-[#4B4B9D] border-[#D5D3EE] hover:bg-[#E1DEF3]";
const BTN_SOFT_BLUE =
  "bg-[#E6F1FA] text-[#39678F] border-[#C5DBEC] hover:bg-[#D7E8F4]";
const BTN_SOLID_GREEN =
  "bg-[#2E8B40] text-white border-transparent hover:bg-[#256E33]";

function triggerBlobDownload(data: Blob, filename: string) {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const today = () => new Date().toISOString().split("T")[0];

/** Short reason for a failed export (server message when there is one). */
const errText = (err: any): string =>
  err?.message && !/status code/i.test(err.message) ? err.message : "Please try again.";

/**
 * Shared "Export Report" card — single source of truth used by BOTH the
 * Calculator and the Production Gantt page. All three exports build their
 * payloads from the LIVE persisted state, so the card behaves identically on
 * either page:
 *   • Throughput Schedule — Production Gantt PDF
 *   • Calculator Print Out — two-column Calculator data sheet
 *   • Combined Report — both of the above merged into one multi-page PDF
 */
export const ExportReportCard: React.FC<ExportReportCardProps> = ({
  modelCode, nFreezers, ready, current = true, page = "gantt",
}) => {
  const [exporting, setExporting] = useState<"" | "gantt" | "calc" | "combined">("");
  // Settings → PDF units. Display only: params / saved scenarios stay SI.
  const { pdfUnits } = useSettings();

  // Additive, fire-and-forget — persist the full scenario (inputs + outputs +
  // charts) on every PDF export, mirroring Save Project. Never blocks/affects
  // the PDF download; failures are swallowed.
  const fireScenarioSave = useCallback(
    (g: { params: any; model_code: string; n_freezers: number }) => {
      saveScenario({
        params: g.params,
        model_code: g.model_code,
        label: `${g.n_freezers} x ${g.model_code}`,
        page,
        trigger: "pdf_export",
        session_id: getClientId(),
      }).catch((err) => console.warn("Scenario save failed (ignored):", err));
    },
    [page],
  );

  const handleExportGantt = useCallback(async () => {
    setExporting("gantt");
    try {
      const g = await buildGanttExportPayload();
      fireScenarioSave(g);
      const { data } = await http.post(
        "/batches/pdf_report",
        { params: g.params, model_code: g.model_code, units: pdfUnits },
        { responseType: "blob", timeout: LONG_TIMEOUT_MS },
      );
      triggerBlobDownload(data as Blob, `Throughput-Schedule ${g.n_freezers} x ${g.model_code} ${today()}.pdf`);
    } catch (err) {
      console.error("Throughput Schedule PDF export failed:", err);
      toast.error("Could not generate the Throughput Schedule PDF.", { description: errText(err) });
    } finally {
      setExporting("");
    }
  }, [fireScenarioSave, pdfUnits]);

  const handleExportCalc = useCallback(async () => {
    setExporting("calc");
    try {
      const [payload, g] = await Promise.all([
        buildCalculatorPdfPayloadLive(pdfUnits),
        buildGanttExportPayload(),
      ]);
      fireScenarioSave(g);
      const { data } = await http.post(
        "/calculator_pdf", payload, { responseType: "blob", timeout: LONG_TIMEOUT_MS },
      );
      const nFr = String(payload.n_freezers || "?").replace(/!/g, "");
      triggerBlobDownload(data as Blob, `Calculator-Printout ${nFr} x ${payload.model_code} ${today()}.pdf`);
    } catch (err) {
      console.error("Calculator Print Out PDF export failed:", err);
      toast.error("Could not generate the Calculator Print Out PDF.", { description: errText(err) });
    } finally {
      setExporting("");
    }
  }, [fireScenarioSave, pdfUnits]);

  const handleExportCombined = useCallback(async () => {
    setExporting("combined");
    try {
      const [calc, gantt] = await Promise.all([
        buildCalculatorPdfPayloadLive(pdfUnits),
        buildGanttExportPayload(),
      ]);
      fireScenarioSave(gantt);
      const { data } = await http.post(
        "/combined_pdf",
        { calc, params: gantt.params, model_code: gantt.model_code, units: pdfUnits },
        { responseType: "blob", timeout: LONG_TIMEOUT_MS },
      );
      const nFr = String(calc.n_freezers || "?").replace(/!/g, "");
      triggerBlobDownload(data as Blob, `Combined-Report ${nFr} x ${gantt.model_code} ${today()}.pdf`);
    } catch (err) {
      console.error("Combined Report PDF export failed:", err);
      toast.error("Could not generate the Combined Report PDF.", { description: errText(err) });
    } finally {
      setExporting("");
    }
  }, [fireScenarioSave, pdfUnits]);

  const busy = exporting !== "";
  const enabled = ready && current && !busy;
  const overlayLabel =
    exporting === "calc" ? "calculator print out"
    : exporting === "combined" ? "combined report"
    : "throughput schedule";

  return (
    <>
      <SectionCard title="Export Report" testId="panel-export-report">
        <div className="flex flex-col gap-2.5 py-1">

          {/* Model preview */}
          <div className="rounded-md bg-[rgba(57,103,143,0.07)] px-3 py-2">
            <p className="text-[10.5px] font-medium text-[var(--js-text-2)] mb-0.5">Model</p>
            <p data-testid="report-filename-preview" className="text-[12px] font-semibold text-[var(--js-navy)] truncate">
              {!ready ? "\u2014 waiting for first calculation \u2014"
                : !current ? "\u2014 results out of date \u2014"
                : `${String(nFreezers ?? "?").replace(/!/g, "")} \u00d7 ${modelCode ?? ""}`}
            </p>
          </div>

          {/* Throughput Schedule — soft purple */}
          <button
            type="button"
            data-testid="export-pdf-button"
            onClick={handleExportGantt}
            disabled={!enabled}
            className={`${BTN_BASE} ${BTN_SOFT_PURPLE}`}
            title="Generate the Production Gantt throughput schedule as a PDF"
          >
            {exporting === "gantt" ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating&hellip;</>
            ) : (
              <><FileDown className="h-3.5 w-3.5" /> Throughput Schedule</>
            )}
          </button>

          {/* Calculator Print Out — soft Jackstone blue */}
          <button
            type="button"
            data-testid="export-calc-pdf-button"
            onClick={handleExportCalc}
            disabled={!enabled}
            className={`${BTN_BASE} ${BTN_SOFT_BLUE}`}
            title="Generate the Calculator Print Out (two-column data sheet) as a PDF"
          >
            {exporting === "calc" ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating&hellip;</>
            ) : (
              <><FileText className="h-3.5 w-3.5" /> Calculator Print Out</>
            )}
          </button>

          {/* Combined Report — solid green */}
          <button
            type="button"
            data-testid="export-combined-pdf-button"
            onClick={handleExportCombined}
            disabled={!enabled}
            className={`${BTN_BASE} ${BTN_SOLID_GREEN}`}
            title="Generate one multi-page PDF: Calculator Print Out + Throughput Schedule"
          >
            {exporting === "combined" ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating&hellip;</>
            ) : (
              <><FileStack className="h-3.5 w-3.5" /> Combined Report</>
            )}
          </button>

          <p className="text-[10.5px] text-[var(--js-text-2)] leading-relaxed">
            <b>Throughput Schedule</b> — A4 freezer schedule, product buffer &amp; throughput-vs-flow charts.
            <> </>
            <b>Calculator Print Out</b> — two-column A4 data sheet of every calculator value.
            <> </>
            <b>Combined Report</b> — both of the above in one multi-page PDF.
          </p>
          {pdfUnits === "US" && (
            <p data-testid="export-pdf-units-hint" className="text-[10.5px] text-[var(--js-text-2)]">
              PDF units: <b>US customary</b> (change in Settings)
            </p>
          )}
        </div>
      </SectionCard>

      {busy && (
        <div
          data-testid="export-overlay"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(51,66,79,0.45)] backdrop-blur-[2px] cursor-wait"
        >
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-white px-8 py-7 shadow-2xl border border-[var(--js-border)]">
            <Loader2 className="h-9 w-9 animate-spin text-[var(--js-blue)]" />
            <p className="text-[14px] font-bold text-[var(--js-blue)]">Generating report&hellip;</p>
            <p className="text-[12px] font-medium text-[var(--js-text-2)]">Rendering the {overlayLabel} PDF</p>
          </div>
        </div>
      )}
    </>
  );
};
